import CreateNavCode from '@/pages/Admin/Bundles/BundleDetails/NavigationCodes/CreateNavCode/CreateNavCode';
import NavigationCodes from '@/pages/Admin/Bundles/BundleDetails/NavigationCodes/NavigationCodes';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { ReactElement, useMemo } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import Toast from './components/Toast/Toast';
import { STKProvider, STKProviderConfig } from './context/context';
import './i18n';
import './index.css';
import AdminBundleDetails from './pages/Admin/Bundles/BundleDetails/BundleDetails';
import NationalProgramDetails from './pages/Admin/Bundles/BundleDetails/NationalProgramDetails/NationalProgramDetails';
import BundleAddOns from './pages/Admin/Bundles/BundleAddOns/BundleAddOns';
import AdminBundleLibrary from './pages/Admin/Bundles/BundleLibrary/BundleLibrary';
import CreateLinkedBundle from './pages/Admin/Bundles/BundleLibrary/CreateLinkedBundle/CreateLinkedBundle';
import { BundleUpcharges } from './pages/Admin/Bundles/BundleUpcharges/BundleUpcharges';
import FZBundleUpcharges from './pages/Franchisee/Bundles/BundleUpcharges/FZBundleUpcharges';
import FZBundleUpchargesStores from './pages/Franchisee/Bundles/BundleUpcharges/FZBundleUpchargesStores';
import FZBundleAddOns from './pages/Franchisee/Bundles/BundleAddOns/FZBundleAddOns';
import FZBundleAddOnsView from './pages/Franchisee/Bundles/BundleAddOns/FZBundleAddOnsView';
import AdminDashboard from './pages/Admin/Dashboard/Dashboard';
import FranchiseePortal from './pages/Admin/FranchiseePortal/FranchiseePortal';
import GlobalFreeze from './pages/Admin/GlobalFreeze/GlobalFreeze';
import AdminMenus from './pages/Admin/Menus/Menus';
import AdminStoreMenuDetails from './pages/Admin/Menus/StoreMenuDetails/AdminStoreMenuDetails';
import NationalModifiers from './pages/Admin/NationalModifiers/NationalModifiers';
import NationalProducts from './pages/Admin/NationalProducts/NationalProducts';
import AdminActivePromotions from './pages/Admin/Promotions/ActivePromotions';
import CERPromotions from './pages/Admin/Promotions/CER/CERPromotions';
import AdminPromotionDetails from './pages/Admin/Promotions/PromotionDetails/PromotionDetails';
import AdminSubmissionDetails from './pages/Admin/Submissions/SubmissionDetails/SubmissionDetails';
import AdminSubmissions from './pages/Admin/Submissions/Submissions';
import Users from './pages/Admin/Users/Users';
import CognitoCallback from './pages/CognitoCallback/CognitoCallback';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import ActiveBundles from './pages/Franchisee/Bundles/Active/ActiveBundles';
import ActiveBundlesTable from './pages/Franchisee/Bundles/Active/ActiveBundlesTable';
import FranchiseeBundleDetails from './pages/Franchisee/Bundles/BundleDetails/BundleDetails';
import FranchiseeBundleLibrary from './pages/Franchisee/Bundles/BundleLibrary/BundleLibrary';
import Calendar from './pages/Franchisee/CalendarPage/CalendarPage';
import FranchiseeDashboard from './pages/Franchisee/Dashboard/Dashboard';
import FranchiseeMenus from './pages/Franchisee/Menus/NewMenus';
import ModifierMenu from './pages/Franchisee/Menus/ModifierMenu';
import FZStoreMenuDetails from './pages/Franchisee/Menus/StoreMenuDetails/FZStoreMenuDetails';
import MyOrganization from './pages/Franchisee/MyOrganization/MyOrganization';
import MyProfile from './pages/Franchisee/MyProfile/MyProfile';
import MyStores from './pages/Franchisee/MyStores/MyStores';
import FranchiseePromotionDetails from './pages/Franchisee/Promotions/PromotionDetails/PromotionDetails';
import FranchiseeActivePromotions from './pages/Franchisee/Promotions/Promotions';
import FZSubmissionDetails from './pages/Franchisee/Submissions/FZSubmissionDetails/FZSubmissionDetails';
import Submissions from './pages/Franchisee/Submissions/Submissions';
import Login from './pages/Login/Login';
import RouteByUserType from './pages/RouteByUserType/RouteByUserType';
import WithAuth from './pages/WithAuth/WithAuth';
import createQueryClient from './queryClient';
import { StoreOnboarding } from './pages/Admin/StoreOnboarding/StoreOnboarding';
import FZStoreMenuModifiersDetails from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/FZStoreMenuModifiersDetails';
import FeatureFlagProtectedRoute from '@/components/FeatureFlagProtectedRoute/FeatureFlagProtectedRoute';
import { FeatureFlag } from '@/services/featureFlags/featureFlags.service';

interface ICustomPages {
  adminBundleLibrary?: ReactElement;
  franchiseeBundleLibrary?: ReactElement;
}

const createRouter = (custom: ICustomPages) =>
  createBrowserRouter([
    {
      element: <WithAuth />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/',
          element:
            sessionStorage.getItem('aliasInfo') !== null ? (
              <RouteByUserType adminPage={<FranchiseeDashboard />} userPage={<FranchiseeDashboard />} />
            ) : (
              <RouteByUserType adminPage={<AdminDashboard />} userPage={<FranchiseeDashboard />} />
            ),
        },
        {
          path: '/bundles/active',
          element: <RouteByUserType userPage={<ActiveBundles />} />,
          children: [{ path: '/bundles/active/:groupId', element: <ActiveBundlesTable /> }],
        },
        {
          path: '/bundles/add-ons',
          element: (
            <FeatureFlagProtectedRoute flag={FeatureFlag.ENABLE_BUNDLE_ADD_ONS} redirectPath="/bundles/library">
              <RouteByUserType adminPage={<BundleAddOns />} userPage={<FZBundleAddOns />} />
            </FeatureFlagProtectedRoute>
          ),
        },
        {
          path: '/bundles/add-ons/view',
          element: (
            <FeatureFlagProtectedRoute flag={FeatureFlag.ENABLE_BUNDLE_ADD_ONS} redirectPath="/bundles/library">
              <RouteByUserType adminPage={<BundleAddOns />} userPage={<FZBundleAddOnsView />} />
            </FeatureFlagProtectedRoute>
          ),
        },
        {
          path: '/bundles/:bundleId',
          element: <RouteByUserType adminPage={<AdminBundleDetails />} userPage={<FranchiseeBundleDetails />} />,
        },
        {
          path: '/bundles/:bundleId/nationalprograms/:nationalProgramId',
          element: <RouteByUserType adminPage={<NationalProgramDetails />} />,
        },
        {
          path: '/bundles/library',
          element: (
            <RouteByUserType
              adminPage={custom.adminBundleLibrary ?? <AdminBundleLibrary />}
              userPage={custom.franchiseeBundleLibrary ?? <FranchiseeBundleLibrary />}
            />
          ),
        },
        {
          path: '/bundles/upcharges',
          element: <RouteByUserType adminPage={<BundleUpcharges />} userPage={<FZBundleUpcharges />} />,
        },
        {
          path: '/bundles/upcharges/stores',
          element: <RouteByUserType adminPage={<BundleUpcharges />} userPage={<FZBundleUpchargesStores />} />,
        },
        {
          path: '/bundles/library/create-linked-bundle',
          element: <RouteByUserType adminPage={<CreateLinkedBundle />} />,
        },
        {
          path: '/bundles/:bundleId/viewNavCode',
          element: <RouteByUserType adminPage={<NavigationCodes />} />,
        },
        {
          path: '/bundles/:bundleId/addNavCode',
          element: <RouteByUserType adminPage={<CreateNavCode />} />,
        },
        {
          path: '/menus',
          element: <RouteByUserType adminPage={<AdminMenus />} userPage={<FranchiseeMenus />} />,
        },
        {
          path: '/modifiers',
          element: <RouteByUserType adminPage={<AdminMenus />} userPage={<ModifierMenu />} />,
        },
        {
          path: '/nationalproducts',
          element: <RouteByUserType adminPage={<NationalProducts />} userPage={<NationalProducts />} />,
        },
        {
          path: '/nationalmodifiers',
          element: <RouteByUserType adminPage={<NationalModifiers />} />,
        },
        {
          path: '/menus/:storeNo',
          element: <RouteByUserType adminPage={<AdminStoreMenuDetails />} userPage={<FZStoreMenuDetails />} />,
        },
        {
          path: '/modifiers/:storeNo',
          element: <RouteByUserType adminPage={<AdminStoreMenuDetails />} userPage={<FZStoreMenuModifiersDetails />} />,
        },
        {
          path: '/menus/manage-menus',
          element: <RouteByUserType adminPage={<AdminStoreMenuDetails />} userPage={<FZStoreMenuDetails />} />,
        },
        {
          path: '/modifiers/manage-menus-modifiers',
          element: <RouteByUserType adminPage={<AdminStoreMenuDetails />} userPage={<FZStoreMenuModifiersDetails />} />,
        },
        {
          path: '/promotions/active',
          element: <RouteByUserType adminPage={<AdminActivePromotions />} userPage={<FranchiseeActivePromotions />} />,
        },
        {
          path: '/promotions/cer',
          element: <RouteByUserType adminPage={<CERPromotions />} />,
        },
        {
          path: '/promotions/:promotionId',
          element: <RouteByUserType adminPage={<AdminPromotionDetails />} userPage={<FranchiseePromotionDetails />} />,
        },
        { path: '/users', element: <RouteByUserType adminPage={<Users />} /> },
        { path: '/franchiseeportal', element: <RouteByUserType adminPage={<FranchiseePortal />} /> },
        { path: '/storeonboarding', element: <RouteByUserType adminPage={<StoreOnboarding />} /> },
        { path: '/calendar', element: <RouteByUserType userPage={<Calendar />} /> },
        { path: '/freezes', element: <RouteByUserType adminPage={<GlobalFreeze />} /> },
        {
          path: '/submissions',
          element: <RouteByUserType userPage={<Submissions />} adminPage={<AdminSubmissions />} />,
        },
        {
          path: '/submissions/:orgId/:submissionId',
          element: <RouteByUserType adminPage={<AdminSubmissionDetails />} />,
        },
        //userPage component routes to a submission's id, not an orgId as declared in path
        {
          path: '/submissions/:orgId',
          element: <RouteByUserType adminPage={<AdminSubmissionDetails />} userPage={<FZSubmissionDetails />} />,
        },
        { path: '/account', element: <Navigate to="/account/profile" /> },
        { path: '/account/profile', element: <RouteByUserType userPage={<MyProfile />} /> },
        { path: '/account/stores', element: <RouteByUserType userPage={<MyStores />} /> },
        { path: '/account/org', element: <RouteByUserType userPage={<MyOrganization />} /> },
        {
          path: '/account/logout',
          element: <Navigate to="/login" />,
        },
      ],
    },
    { path: '/login', element: <Login />, errorElement: <ErrorPage /> },
    { path: '/cognito/callback', element: <CognitoCallback />, errorElement: <ErrorPage /> },
  ]);

const STKFE: React.FC<{ config: STKProviderConfig; pages?: ICustomPages }> = ({ config, pages }) => {
  const router = useMemo(() => createRouter(pages ?? {}), [pages]);
  const queryClient = createQueryClient();

  return (
    <STKProvider config={config} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toast />
      </QueryClientProvider>
    </STKProvider>
  );
};

export default STKFE;
